import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { useGuestAuth } from '../contexts/GuestAuthContext';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * Favorite/Heart Button Component
 * 
 * Allows signed-in guests to save properties to their favorites.
 * If not signed in, clicking will redirect to Google sign-in.
 */
const FavoriteButton = ({ 
  propertyId, 
  variant = 'icon', // 'icon' | 'button'
  size = 'default',
  className = ''
}) => {
  const { isFavorite, toggleFavorite, isAuthenticated } = useGuestAuth();
  const [isLoading, setIsLoading] = useState(false);
  const favorited = isFavorite(propertyId);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsLoading(true);
    try {
      await toggleFavorite(propertyId);
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'button') {
    return (
      <Button
        variant="outline"
        size={size}
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          'gap-2',
          favorited && 'border-red-300 bg-red-50 hover:bg-red-100',
          className
        )}
        data-testid={`favorite-btn-${propertyId}`}
      >
        <Heart 
          className={cn(
            'h-4 w-4 transition-all',
            favorited ? 'fill-red-500 text-red-500' : 'text-slate-500',
            isLoading && 'animate-pulse'
          )} 
        />
        {favorited ? 'Saved' : 'Save'}
      </Button>
    );
  }

  // Icon variant (default)
  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        'p-2 rounded-full transition-all',
        'hover:bg-white/90 active:scale-95',
        favorited ? 'bg-white shadow-lg' : 'bg-white/80',
        className
      )}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      data-testid={`favorite-icon-${propertyId}`}
    >
      <Heart 
        className={cn(
          'h-5 w-5 transition-all',
          favorited ? 'fill-red-500 text-red-500' : 'text-slate-600 hover:text-red-500',
          isLoading && 'animate-pulse'
        )} 
      />
    </button>
  );
};

export default FavoriteButton;
